import { HttpStatusCode } from 'axios';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../shared/Buttons';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { CreateDealRequest } from '../../../../../types/apiTypes';
import { VendorBusinessLocation } from '../../../../../types/dbTypes';
import { InputNumber, Select } from 'antd';

interface CreatePromotionProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  locations: VendorBusinessLocation[];
}

interface CreatePromotionForm {
  dealType: string;
  title: string;
  description: string;
  discountPercentage?: number;
  originalPrice?: number;
  startAt?: Date;
  endAt?: Date;
  locations: string[];
  indefinite: boolean;
}

const dealTypeOptions = [
  { value: 'labor', label: 'Labor Discount' },
  { value: 'service', label: 'Service Discount' },
  { value: 'goods', label: 'Parts or Goods Discount' },
];

function CreatePromotion({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  locations,
}: CreatePromotionProps) {
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<CreatePromotionForm>();
  const { userId } = useUser();
  const navigate = useNavigate();
  const indefinite = watch('indefinite');
  const createPromotion = async (
    dealInfo: CreatePromotionForm,
    vendor_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/deals',
        'post',
        {
          vendor_id: vendor_id,
          deal_type: dealInfo?.dealType,
          title: dealInfo?.title,
          description: dealInfo?.description,
          discount_percentage: dealInfo?.discountPercentage,
          original_price: dealInfo?.originalPrice,
          start_at: new Date(dealInfo?.startAt ?? ''),
          end_at: !dealInfo?.indefinite
            ? new Date(dealInfo?.endAt ?? '')
            : undefined,
          locations: dealInfo?.locations,
        } as CreateDealRequest,
        'Error occurred while creating your promotion.'
      );
      const data = response?.data?.data?.deal;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        navigate('/dashboard/promotions');
        toast.success('Promotion created successfully!');
      }
    } catch (e: any) {
      console.error('Error occurred while creating your new promotion: ', e);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<CreatePromotionForm> = (data) => {
    createPromotion(data, userId ?? '');
  };
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full items-center justify-center pt-[16px]">
            <div className="flex h-full flex-col items-center justify-between space-y-[16px] bg-white sm:w-[95%] sm:max-w-[352px] md:w-full md:max-w-[520px] md:px-4 lg:w-[1148px] lg:max-w-[1148px]">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className={`flex h-full w-full flex-col items-center space-y-[16px] overflow-y-auto overflow-x-hidden bg-white p-6 lg:w-[1148px] lg:max-w-[1148px]`}
              >
                <div className="w-full">
                  <label
                    htmlFor="title"
                    className="block text-[16px] text-graysecondary"
                  >
                    Promotion Title
                  </label>
                  <input
                    type="text"
                    placeholder="15% Off Tires"
                    {...register('title', {
                      required: 'Title is required',
                    })}
                    id="title"
                    className={`mt-1 block w-full border px-3 py-2 ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.title.message}
                    </p>
                  )}
                </div>
                <div className="flex w-full items-center gap-4 align-middle sm:flex-col md:flex-row">
                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="startAt"
                      className="block text-[16px] text-graysecondary"
                    >
                      Start Date
                    </label>
                    <input
                      type="date"
                      {...register('startAt', {
                        required: 'Start Date is required',
                      })}
                      id="startAt"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.startAt ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.startAt && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.startAt.message}
                      </p>
                    )}
                  </div>

                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="endAt"
                      className="block text-[16px] text-graysecondary"
                    >
                      End Date
                    </label>
                    <input
                      disabled={indefinite}
                      type="date"
                      {...register('endAt')}
                      id="endAt"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.endAt ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.endAt && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.endAt.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="ml-4 flex w-full gap-4 align-middle sm:flex-col sm:items-start sm:justify-start md:flex-row md:items-end md:justify-end">
                  <div className="sm:w-full md:w-[50%]">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        {...register('indefinite')}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Indefinite
                      </span>
                    </label>
                  </div>
                </div>
                <div className="flex w-full items-end gap-4 align-middle sm:flex-col md:flex-row">
                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="dealType"
                      className="block text-[16px] text-graysecondary"
                    >
                      Deal Type
                    </label>
                    <Controller
                      name="dealType"
                      control={control}
                      rules={{ required: 'Deal Type is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder="Select Deal Type"
                          className="h-[38px] items-center align-middle"
                          style={{ width: '100%' }}
                          options={dealTypeOptions}
                          onChange={(value) => field.onChange(value)} // Update field value on change
                        />
                      )}
                    />
                    {errors.dealType && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.dealType.message}
                      </p>
                    )}
                  </div>
                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="locations"
                      className="block text-[16px] text-graysecondary"
                    >
                      Select Locations
                    </label>
                    <Controller
                      name="locations"
                      control={control}
                      rules={{ required: 'At least one location is required' }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          mode="multiple"
                          className="h-[38px]"
                          allowClear
                          placeholder="Please select locations"
                          maxTagCount={0}
                          maxTagPlaceholder={(omittedValues) =>
                            `+${omittedValues.length} more`
                          }
                          style={{ width: '100%' }}
                          options={locations.map((loc) => ({
                            label: `${loc.address_st_number} ${loc.address_st_name?.slice(0, 13) + (loc.address_st_name && loc.address_st_name?.length > 13 ? '...' : '')}${
                              loc.address_unit ? ` #${loc.address_unit}` : ''
                            }, ${loc.address_city}, ${loc.address_state} ${loc.address_zip}`,
                            value: loc.location_id,
                          }))}
                          onChange={(value) => field.onChange(value)} // Update field value on change
                        />
                      )}
                    />
                    {errors.locations && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.locations.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-full items-end gap-4 align-middle sm:flex-col md:flex-row">
                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="originalPrice"
                      className="block text-[16px] text-graysecondary"
                    >
                      Original Price
                    </label>
                    <Controller
                      name="originalPrice"
                      control={control}
                      rules={{ required: 'Original Price is required' }}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          className="flex h-[38px] items-center align-middle"
                          min={1}
                          style={{ width: '100%' }}
                          placeholder="Enter original price"
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                    {errors.originalPrice && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.originalPrice.message}
                      </p>
                    )}
                  </div>

                  {/* Discount Percentage */}
                  <div className="sm:w-full md:w-[50%]">
                    <label
                      htmlFor="discountPercentage"
                      className="block text-[16px] text-graysecondary"
                    >
                      Discount Percentage
                    </label>
                    <Controller
                      name="discountPercentage"
                      control={control}
                      rules={{
                        required: 'Discount Percentage is required',
                        min: { value: 0, message: 'Cannot be less than 0%' },
                        max: {
                          value: 100,
                          message: 'Cannot be more than 100%',
                        },
                      }}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          className="flex h-[38px] items-center align-middle"
                          min={0}
                          max={100}
                          formatter={(value) => `${value}%`}
                          parser={(value) =>
                            parseFloat(value?.replace('%', '') || '')
                          }
                          style={{ width: '100%' }}
                          placeholder="Enter discount percentage"
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                    {errors.discountPercentage && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.discountPercentage.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <label
                    htmlFor="description"
                    className="block text-[16px] text-graysecondary"
                  >
                    Promotion Description
                  </label>
                  <textarea
                    {...register('description', {
                      required: 'Description is required',
                    })}
                    id="description"
                    placeholder="Describe the details of the promotion"
                    className={`mt-1 block min-h-[150px] w-full border px-3 py-2 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </form>
              <div className="flex h-[80px] max-h-[80px] min-h-[80px] w-full flex-row items-center justify-center gap-x-[16px] border-t-[1px] border-[#EAECF0] px-4 align-middle">
                <NeutralButton
                  onClick={() => navigate('/dashboard/promotions')}
                  addClass="rounded-full w-[250px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  addClass={`h-[48px] w-[250px] rounded-full`}
                  submit={true}
                  onClick={handleSubmit(onSubmit)}
                >
                  {!loading ? (
                    'Add Promotion'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CreatePromotion;
